import { useEffect } from 'react';
import { Capacitor } from '@capacitor/core';
import { SplashScreen } from '@capacitor/splash-screen';
import { StatusBar, Style } from '@capacitor/status-bar';
import { App as CapApp } from '@capacitor/app';

/**
 * Native lifecycle bootstrap for the Nia app.
 *
 * Runs once when the AuthProvider mounts. On native (iOS/Android) it:
 *   - Sets the status bar style to "light text on brand teal".
 *   - Hides the splash screen with a short fade (we kept `launchAutoHide=false`
 *     in capacitor.config.ts so React owns the timing — no awkward white flash).
 *   - Wires the Android hardware back button: if the WebView can go back, do
 *     that; otherwise exit the app politely.
 *   - Listens for deep links (e.g. `nia://family/abc`) and routes them.
 *
 * On the web this hook is a no-op (Capacitor's web shims return early).
 */
export default function useNativeBootstrap() {
    useEffect(() => {
        if (!Capacitor.isNativePlatform()) return;

        let backHandler;
        let urlHandler;
        let cancelled = false;

        (async () => {
            try {
                // Brand-teal status bar with light foreground content.
                await StatusBar.setStyle({ style: Style.Dark });
                await StatusBar.setBackgroundColor({ color: '#0A2E28' });
                await StatusBar.setOverlaysWebView({ overlay: false });
            } catch (e) {
                console.debug('StatusBar plugin unavailable (expected on web)', e);
            }
            // A small delay so the React shell paints first and we don't show
            // an empty page during the fade-out.
            await new Promise((r) => setTimeout(r, 250));
            if (cancelled) return;
            try { await SplashScreen.hide({ fadeOutDuration: 240 }); } catch (e) { console.debug('SplashScreen plugin unavailable (expected on web)', e); }

            // Android back-button: defer to history when possible.
            backHandler = await CapApp.addListener('backButton', ({ canGoBack }) => {
                if (canGoBack) {
                    window.history.back();
                } else {
                    CapApp.exitApp();
                }
            });

            urlHandler = await CapApp.addListener('appUrlOpen', (event) => {
                try {
                    const url = new URL(event.url);
                    // Strip scheme + host; React Router consumes the path/query.
                    const path = `${url.pathname}${url.search}` || '/';
                    if (window.location.pathname !== path) {
                        window.history.pushState({}, '', path);
                        window.dispatchEvent(new PopStateEvent('popstate'));
                    }
                } catch (e) {
                    console.debug('appUrlOpen: unparseable URL, ignoring', e);
                }
            });
        })();

        return () => {
            cancelled = true;
            if (backHandler && typeof backHandler.remove === 'function') backHandler.remove();
            if (urlHandler && typeof urlHandler.remove === 'function') urlHandler.remove();
        };
    }, []);
}
